import { useRef, useState, useCallback, useEffect } from "react";

/**
 * HeaderDragCanvas
 * ----------------
 * A single Letter-page mini canvas that renders the three header elements
 * — logo, business name, document title — as independently draggable +
 * selectable overlays. Selecting an element shows its handles (drag, and,
 * for the logo, a corner resize handle that preserves aspect ratio).
 *
 * When the user drags/resizes:
 *   • The element's positioning mode is auto-switched to "free" so its X/Y%
 *     are honored by the PDF renderer.
 *   • Position is stored as percentages of the printable page area.
 *   • For text elements, resize is exposed as a font-size stepper next to
 *     the selection tag.
 *
 * All layout changes flow through onChange({ patches: {...} }) — patches
 * is a shallow map of `layout.<field>` keys to new values.
 *
 * Every interactive control has a stable data-testid.
 */

const PREVIEW_H = 380;
const PREVIEW_W = Math.round(PREVIEW_H * (8.5 / 11));
const PAGE_RENDER_W = 850; // matches the Letter width our documents render at
const SCALE = PREVIEW_W / PAGE_RENDER_W;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function HeaderDragCanvas({
  layout,
  content,
  effectiveLogoUrl,
  effectiveBusinessName,
  activeTitle, // "Estimate" or "Invoice" (the currently selected preview kind's visible title)
  onChange,
  hasLogo,
}) {
  const canvasRef = useRef(null);
  const [selected, setSelected] = useState(null); // 'logo' | 'biz_name' | 'title' | null
  const [natRatio, setNatRatio] = useState(1); // logo aspect ratio for resize

  const showLogo = hasLogo && layout?.logo_position !== "hidden";
  const showBizName = layout?.show_business_name !== false && !!effectiveBusinessName;
  const showTitle = layout?.show_title !== false && !!activeTitle;

  // ---- Element coords helper ----------------------------------------------
  const coords = {
    logo: {
      x: layout?.logo_layout_mode === "free" ? (layout?.logo_free_x ?? 0) : 2,
      y: layout?.logo_layout_mode === "free" ? (layout?.logo_free_y ?? 0) : 2,
      mode: layout?.logo_layout_mode || "anchored",
    },
    biz_name: {
      x: layout?.business_name_layout_mode === "free" ? (layout?.business_name_free_x ?? 0) : 4,
      y: layout?.business_name_layout_mode === "free" ? (layout?.business_name_free_y ?? 0) : 18,
      mode: layout?.business_name_layout_mode || "anchored",
    },
    title: {
      x: layout?.title_layout_mode === "free" ? (layout?.title_free_x ?? 0) : 4,
      y: layout?.title_layout_mode === "free" ? (layout?.title_free_y ?? 0) : 25,
      mode: layout?.title_layout_mode || "anchored",
    },
  };

  const modeKey = { logo: "logo_layout_mode", biz_name: "business_name_layout_mode", title: "title_layout_mode" };
  const xKey = { logo: "logo_free_x", biz_name: "business_name_free_x", title: "title_free_x" };
  const yKey = { logo: "logo_free_y", biz_name: "business_name_free_y", title: "title_free_y" };

  // ---- Drag ----------------------------------------------------------------
  const startDrag = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(id);
    const box = canvasRef.current.getBoundingClientRect();
    // Grab-offset relative to element origin (in canvas px)
    const elLeftPx = (coords[id].x / 100) * box.width;
    const elTopPx = (coords[id].y / 100) * box.height;
    const dx = e.clientX - box.left - elLeftPx;
    const dy = e.clientY - box.top - elTopPx;

    let didMove = false;
    const onMove = (ev) => {
      didMove = true;
      const newLeftPx = clamp(ev.clientX - box.left - dx, 0, box.width);
      const newTopPx = clamp(ev.clientY - box.top - dy, 0, box.height);
      const nx = (newLeftPx / box.width) * 100;
      const ny = (newTopPx / box.height) * 100;
      const patches = {};
      patches[`layout.${xKey[id]}`] = Math.round(nx * 100) / 100;
      patches[`layout.${yKey[id]}`] = Math.round(ny * 100) / 100;
      // Auto-flip to free positioning the first time an anchored element is
      // dragged so the render pipeline honors the new coords.
      if (coords[id].mode !== "free") patches[`layout.${modeKey[id]}`] = "free";
      onChange(patches);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // If it was a click without movement, we already set `selected` above.
      void didMove;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---- Logo resize (aspect preserved) --------------------------------------
  const startLogoResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startHeight = layout?.logo_size_px ?? 64;
    const onMove = (ev) => {
      const delta = Math.max(ev.clientX - startX, ev.clientY - startY);
      const newH = clamp(startHeight + delta / SCALE, 24, 320);
      onChange({ "layout.logo_size_px": Math.round(newH), "layout.logo_width_px": null });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---- Text font-size stepper ---------------------------------------------
  const bumpFontSize = (which, delta) => {
    const field = which === "biz_name" ? "business_name_font_size_px" : "title_font_size_px";
    const current = layout?.[field] ?? (which === "biz_name" ? 14 : 28);
    const next = clamp(current + delta, 8, 80);
    onChange({ [`layout.${field}`]: next });
  };

  const hideElement = (id) => {
    if (id === "logo") onChange({ "layout.logo_position": "hidden" });
    if (id === "biz_name") onChange({ "layout.show_business_name": false });
    if (id === "title") onChange({ "layout.show_title": false });
    setSelected(null);
  };

  const alignElement = (id, align) => {
    if (id === "biz_name") onChange({ "layout.business_name_align": align });
    if (id === "title") onChange({ "layout.title_align": align });
  };

  // ---- Compute rendered sizes ---------------------------------------------
  const logoPreviewH = (layout?.logo_size_px ?? 64) * SCALE;
  const logoPreviewW = logoPreviewH * (natRatio || 1);
  const bizFontSize = (layout?.business_name_font_size_px ?? 14) * SCALE;
  const titleFontSize = (layout?.title_font_size_px ?? 28) * SCALE;

  // Deselect if user hides the currently selected element from elsewhere
  useEffect(() => {
    if (selected === "logo" && !showLogo) setSelected(null);
    if (selected === "biz_name" && !showBizName) setSelected(null);
    if (selected === "title" && !showTitle) setSelected(null);
  }, [selected, showLogo, showBizName, showTitle]);

  const SelectRing = ({ id }) => selected === id ? (
    <div className="absolute inset-0 pointer-events-none ring-2 ring-[#FF5A00] ring-offset-1 rounded-sm" />
  ) : null;

  return (
    <div className="space-y-2">
      {/* Selection banner + tool row */}
      <div className="flex items-center justify-between gap-2 text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">
        <div data-testid="header-canvas-selected-label">
          {selected
            ? `Selected: ${selected === "logo" ? "Logo" : selected === "biz_name" ? "Business name" : "Document title"}`
            : "Click any element to select it"}
        </div>
        {selected && (
          <button
            type="button"
            className="px-2 py-0.5 border border-rose-200 text-rose-600 rounded hover:bg-rose-50"
            onClick={() => hideElement(selected)}
            data-testid={`header-canvas-remove-${selected}`}
          >
            Remove
          </button>
        )}
      </div>

      {/* Inline formatting toolbar when a text element is selected */}
      {(selected === "biz_name" || selected === "title") && (
        <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-md px-2 py-1.5" data-testid={`header-canvas-toolbar-${selected}`}>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">A</span>
          <button type="button" className="w-6 h-6 border border-zinc-200 rounded text-xs hover:border-[#FF5A00]" onClick={() => bumpFontSize(selected, -1)} data-testid={`header-canvas-font-shrink-${selected}`}>−</button>
          <span className="text-xs font-mono w-8 text-center" data-testid={`header-canvas-font-value-${selected}`}>
            {selected === "biz_name" ? (layout?.business_name_font_size_px ?? 14) : (layout?.title_font_size_px ?? 28)}
          </span>
          <button type="button" className="w-6 h-6 border border-zinc-200 rounded text-xs hover:border-[#FF5A00]" onClick={() => bumpFontSize(selected, +1)} data-testid={`header-canvas-font-grow-${selected}`}>+</button>
          <div className="w-px h-4 bg-zinc-200 mx-1"/>
          {[["left","L"],["center","C"],["right","R"]].map(([k, glyph]) => (
            <button
              key={k}
              type="button"
              className={`w-6 h-6 text-[10px] border rounded hover:border-[#FF5A00] ${
                (selected === "biz_name" ? layout?.business_name_align : layout?.title_align) === k
                  ? "border-[#FF5A00] text-[#FF5A00]"
                  : "border-zinc-200"
              }`}
              onClick={() => alignElement(selected, k)}
              data-testid={`header-canvas-align-${selected}-${k}`}
            >
              {glyph}
            </button>
          ))}
        </div>
      )}

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="relative bg-white border border-zinc-300 rounded-md mx-auto overflow-hidden select-none"
        style={{ width: PREVIEW_W, height: PREVIEW_H }}
        data-testid="header-drag-canvas"
        onMouseDown={(e) => { if (e.target === canvasRef.current) setSelected(null); }}
      >
        {/* Faint page grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "linear-gradient(#f4f4f5 1px, transparent 1px), linear-gradient(90deg, #f4f4f5 1px, transparent 1px)",
          backgroundSize: `${PREVIEW_W / 8}px ${PREVIEW_H / 11}px`,
          opacity: 0.6,
        }} />
        <div className="absolute left-3 right-3 top-3 text-[9px] uppercase tracking-widest text-zinc-300 font-mono pointer-events-none">Page canvas · Letter</div>

        {/* Logo */}
        {showLogo && (
          <div
            className={`absolute cursor-move group ${selected === "logo" ? "" : "hover:ring-1 hover:ring-orange-300"}`}
            style={{
              left: `${coords.logo.x}%`,
              top: `${coords.logo.y}%`,
              width: logoPreviewW || 40,
              height: logoPreviewH || 30,
            }}
            onMouseDown={startDrag("logo")}
            data-testid="header-canvas-logo"
          >
            <img
              src={effectiveLogoUrl}
              alt=""
              onLoad={(e) => {
                const el = e.currentTarget;
                if (el.naturalWidth && el.naturalHeight) setNatRatio(el.naturalWidth / el.naturalHeight);
              }}
              className="w-full h-full object-contain pointer-events-none"
              draggable={false}
            />
            {selected === "logo" && (
              <div
                className="absolute -right-1 -bottom-1 w-3 h-3 bg-[#FF5A00] rounded-sm cursor-nwse-resize ring-2 ring-white"
                onMouseDown={startLogoResize}
                data-testid="header-canvas-logo-resize"
              />
            )}
            <SelectRing id="logo" />
          </div>
        )}

        {/* Business name */}
        {showBizName && (
          <div
            className={`absolute cursor-move px-1 ${selected === "biz_name" ? "" : "hover:ring-1 hover:ring-orange-300"}`}
            style={{
              left: `${coords.biz_name.x}%`,
              top: `${coords.biz_name.y}%`,
              fontSize: bizFontSize,
              fontWeight: layout?.business_name_weight === "bold" ? 800 : layout?.business_name_weight === "semibold" ? 600 : 400,
              textAlign: layout?.business_name_align || "left",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
            }}
            onMouseDown={startDrag("biz_name")}
            data-testid="header-canvas-biz-name"
          >
            {effectiveBusinessName}
            <SelectRing id="biz_name" />
          </div>
        )}

        {/* Document title */}
        {showTitle && (
          <div
            className={`absolute cursor-move px-1 ${selected === "title" ? "" : "hover:ring-1 hover:ring-orange-300"}`}
            style={{
              left: `${coords.title.x}%`,
              top: `${coords.title.y}%`,
              fontSize: titleFontSize,
              fontWeight: layout?.title_weight === "bold" ? 800 : layout?.title_weight === "semibold" ? 600 : 400,
              textAlign: layout?.title_align || "left",
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              color: "#18181b",
            }}
            onMouseDown={startDrag("title")}
            data-testid="header-canvas-title"
          >
            {activeTitle}
            <SelectRing id="title" />
          </div>
        )}
      </div>

      {/* Helper text */}
      <p className="text-[10px] text-zinc-400 leading-relaxed">
        Drag the logo, business name, or document title anywhere on the page. Positions are saved with this
        template and applied identically to the preview, customer view, print output, and generated PDFs.
        The underlying document type never changes — an Estimate stays an Estimate; an Invoice stays an Invoice.
      </p>
    </div>
  );
}
